import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../hooks/useLanguage';
import { cn } from '../../utils/cn';
import { homepageContentApi } from '../../services/api';

interface AdvantageItem {
  icon: string;
  title: { zh: string; vi: string };
  description: { zh: string; vi: string };
}

interface StatItem {
  label: { zh: string; vi: string };
  value: string;
}

// 后备数据
const defaultAdvantages: AdvantageItem[] = [
  { icon: '🏭', title: { zh: '工业厂房专业经验', vi: 'Kinh nghiệm chuyên sâu về nhà xưởng công nghiệp' }, description: { zh: '专注工业厂房及外资工厂建筑工程，拥有丰富的本地化施工经验，深刻理解越南工业标准与规范。', vi: 'Chuyên thi công nhà xưởng công nghiệp và nhà máy FDI, có kinh nghiệm thi công nội địa phong phú, hiểu sâu tiêu chuẩn và quy định công nghiệp Việt Nam.' } },
  { icon: '⚡', title: { zh: '电气暖通一体化', vi: 'Tích hợp hệ thống điện và HVAC' }, description: { zh: '提供电气、暖通空调、通风系统一体化解决方案，确保厂房环境舒适高效，降低能耗成本。', vi: 'Cung cấp giải pháp tích hợp hệ thống điện, HVAC và thông gió, đảm bảo môi trường nhà xưởng thoải mái và hiệu quả, giảm chi phí năng lượng.' } },
  { icon: '👷', title: { zh: '本地化专业团队', vi: 'Đội ngũ chuyên gia nội địa' }, description: { zh: '拥有熟悉越南建筑法规的中越双语专业团队，提供从设计到施工的全程服务，沟通无障碍。', vi: 'Sở hữu đội ngũ chuyên gia song ngữ Trung-Việt quen thuộc với quy định xây dựng Việt Nam, cung cấp dịch vụ trọn gói từ thiết kế đến thi công, giao tiếp thuận lợi.' } },
  { icon: '📈', title: { zh: '质量与安全保证', vi: 'Đảm bảo chất lượng và an toàn' }, description: { zh: '严格遵循ISO质量管理体系，所有工程均通过越南当地质量验收，提供长期维护服务。', vi: 'Tuân thủ nghiêm ngặt hệ thống quản lý chất lượng ISO, tất cả công trình đều được nghiệm thu chất lượng tại địa phương Việt Nam, cung cấp dịch vụ bảo trì dài hạn.' } },
  { icon: '🌍', title: { zh: '中资工厂专业服务', vi: 'Dịch vụ chuyên nghiệp cho nhà máy Trung Quốc' }, description: { zh: '特别为中资企业提供定制化建筑解决方案，理解中国文化与越南市场的双重需求，助力企业顺利落地。', vi: 'Đặc biệt cung cấp giải pháp xây dựng tùy chỉnh cho doanh nghiệp Trung Quốc, hiểu rõ nhu cầu kép của văn hóa Trung Quốc và thị trường Việt Nam, hỗ trợ doanh nghiệp triển khai thuận lợi.' } },
  { icon: '💼', title: { zh: '一站式工程服务', vi: 'Dịch vụ công trình trọn gói' }, description: { zh: '从防水地坪漆到建筑装饰改造，提供涵盖电气、暖通、通风、装饰等全方位的工程服务。', vi: 'Từ sơn sàn chống thấm đến cải tạo trang trí công trình, cung cấp dịch vụ công trình toàn diện bao gồm điện, HVAC, thông gió, trang trí.' } }
];

const defaultStats: StatItem[] = [
  { label: { zh: '完成项目', vi: 'Dự án hoàn thành' }, value: '50+' },
  { label: { zh: '合作外资企业', vi: 'Doanh nghiệp FDI hợp tác' }, value: '10+' },
  { label: { zh: '项目验收通过率', vi: 'Tỷ lệ nghiệm thu dự án' }, value: '100%' }
];

const Advantages: React.FC = () => {
  const { language, t } = useLanguage();
  const [advantages, setAdvantages] = useState<AdvantageItem[]>(defaultAdvantages);
  const [stats, setStats] = useState<StatItem[]>(defaultStats);
  const [sectionTitle, setSectionTitle] = useState({ zh: '我们的优势', vi: 'Thế mạnh của chúng tôi' });

  // 从 API 加载数据
  useEffect(() => {
    Promise.all([
      homepageContentApi.getBySection('advantages'),
      homepageContentApi.getBySection('company_stats')
    ]).then(([advRes, statsRes]) => {
      if (advRes.success && advRes.data?.length > 0) {
        const item = advRes.data[0];
        if (item.title_zh) setSectionTitle({ zh: item.title_zh, vi: item.title_vi || sectionTitle.vi });
        try {
          const parsed = JSON.parse(item.content_zh || '[]');
          if (Array.isArray(parsed) && parsed.length > 0) {
            const mapped = parsed.map((a: any) => ({
              icon: a.icon || '✅',
              title: { zh: a.title_zh || '', vi: a.title_vi || '' },
              description: { zh: a.desc_zh || '', vi: a.desc_vi || '' }
            }));
            setAdvantages(mapped);
          }
        } catch { /* 保留默认值 */ }
      }

      if (statsRes.success && statsRes.data?.length > 0) {
        const item = statsRes.data[0];
        try {
          const parsed = JSON.parse(item.content_zh || '[]');
          if (Array.isArray(parsed) && parsed.length > 0) {
            const mapped = parsed.map((s: any) => ({
              label: { zh: s.label_zh || '', vi: s.label_vi || '' },
              value: s.value || '0'
            }));
            setStats(mapped);
          }
        } catch { /* 保留默认值 */ }
      }
    }).catch(() => { /* 使用默认值 */ });
  }, []);

  return (
    <section className="py-16 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {sectionTitle[language as 'zh' | 'vi'] || sectionTitle.zh}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {language === 'zh'
              ? '惠亚建筑凭借专业经验与本地化服务，为客户提供卓越的建筑工程解决方案'
              : 'Hueya Construction với kinh nghiệm chuyên môn và dịch vụ nội địa hóa, cung cấp giải pháp công trình xây dựng xuất sắc cho khách hàng'
            }
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {advantages.map((advantage, index) => (
            <div
              key={index}
              className={cn(
                "bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300",
                "border border-gray-100 hover:border-blue-200",
                "transform hover:-translate-y-1"
              )}
            >
              <div className="flex items-start space-x-4">
                <div className="text-4xl">{advantage.icon}</div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {advantage.title[language as 'zh' | 'vi'] || advantage.title.zh}
                  </h3>
                  <p className="text-gray-600 leading-relaxed">
                    {advantage.description[language as 'zh' | 'vi'] || advantage.description.zh}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-4 md:p-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {stats.slice(0, 3).map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-gray-700 font-medium">
                  {stat.label[language as 'zh' | 'vi'] || stat.label.zh}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Advantages;
